import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    inject,
    signal,
} from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { IAccessRightCoreFilter, IAccessRightSelectionFiltersCollection } from '@fi-shared/models';
import { Subject } from 'rxjs';
import { IAccessRightsCoreFiltersErrors } from '@fi-shared/fi-shared-services/fi-access-right-helper.service';
import { FiValuesComparison } from '@fi-shared/utils/fi-values-comparison';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { fiValidationHasErrors } from '@fi-shared/utils/fi-validation-has-errors';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import {
    IItemSharingSettingsDialogData,
    IItemSharingSettingsDialogResult,
    IItemSharingSettingsValuesComparisonData,
} from './fi-item-sharing-settings';
import {
    FiSharingSettingsContentComponent,
    ISharingSettingsContentConfig,
} from '@fi-shared/access-right-filters/fi-sharing-settings-content/fi-sharing-settings-content.component';
import { AppTextService } from '@fi-shared/app-text';

@Component({
    selector: 'fi-item-sharing-settings-dialog',
    templateUrl: './sharing-settings-dialog.component.html',
    styleUrl: './sharing-settings-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-item-sharing-settings-dialog' },
    standalone: false,
})
export class FiItemSharingSettingsDialogComponent
    extends FiDialogsContentBase<IItemSharingSettingsDialogData, IItemSharingSettingsDialogResult>
    implements OnInit, OnDestroy
{
    @ViewChild('fiSharingSettingsContentComponent')
    protected fiSharingSettingsContentComponent: FiSharingSettingsContentComponent;

    private _manualSelectionValidationErrors: IAccessRightsCoreFiltersErrors = {};
    private fiValuesComparison: FiValuesComparison<IItemSharingSettingsValuesComparisonData>;

    // ? isSaveAsNewQuickSelection$ not used? Remove?
    // protected isSaveAsNewQuickSelection$ = new BehaviorSubject<boolean>(false);

    private readonly fiDialogsService = inject(FiDialogsService);
    private readonly appTextService = inject(AppTextService);

    protected isShared = signal<boolean>(this.data.isShared);
    protected itemAccessRightCoreFilter = signal<IAccessRightCoreFilter>(this.data.itemAccessRightCoreFilter);
    protected savedQuickSelectionFilters = signal<IAccessRightSelectionFiltersCollection[]>(
        this.data.savedQuickSelectionFilters,
    );
    protected selectedFiltersCollection = signal<IAccessRightSelectionFiltersCollection>(
        this.data.selectedFiltersCollection,
    );

    protected readonly config: Readonly<ISharingSettingsContentConfig> = {
        availableAccessRightFilterCollection: this.data.availableAccessRightFilterCollection,
        accessRightUserFilter: this.data.itemAccessRightUserFilter,
        moduleType: this.data.moduleType,
        businessUnitIdsOutOfScope: this.data.businessUnitIdsOutOfScope,
        isItemCreator: this.data.isItemOwner,
        individualUnitSectionDescriptionText: this.data.individualUnitSectionDescriptionText,
        showRolesSelect: false,
        showRolesCheckboxes: this.data.showRolesCheckboxes,
        areasSectionHeaderText: this.appTextService.get('access_rights.manual_selection'),
        individualUnitSectionHeaderText:
            this.data.individualUnitSectionHeaderText ?? this.appTextService.get('sharing.individual_units'),
        quickSelectLabelId: 'access_rights.quick_selection',
    };

    public ngOnInit(): void {
        this.fiValuesComparison = new FiValuesComparison(() => ({
            isShared: this.isShared(),
            itemAccessRightCoreFilter: this.itemAccessRightCoreFilter(),
        }));
        this.subscribeOnTransitionCloseDialog(this.fiDialogsService, this.ngOnDestroy$);
    }

    protected onAccessRightCoreFilterChange(filter: IAccessRightCoreFilter): void {
        this.itemAccessRightCoreFilter.set(filter);
    }

    protected onIsSharedChange(isShared: boolean): void {
        this.isShared.set(isShared);
    }

    protected onSavedQuickSelectionFiltersChange(filters: IAccessRightSelectionFiltersCollection[]): void {
        this.savedQuickSelectionFilters.set(filters);
    }

    protected onSelectedFiltersCollectionChange(collection: IAccessRightSelectionFiltersCollection): void {
        this.selectedFiltersCollection.set(collection);
    }

    protected onValidationErrorsChange(errors: IAccessRightsCoreFiltersErrors): void {
        this._manualSelectionValidationErrors = errors;
    }

    public isYesButtonDisabled(): boolean {
        if (!this.fiSharingSettingsContentComponent) {
            return true;
        }

        if (!!this.itemAccessRightCoreFilter().BusinessUnitIds?.length) {
            return this.isNewSelectionWithoutName();
        }

        return fiValidationHasErrors(this._manualSelectionValidationErrors) || this.isNewSelectionWithoutName();
    }

    private isNewSelectionWithoutName(): boolean {
        return (
            this.fiSharingSettingsContentComponent.isSaveAsNewQuickSelection() &&
            !this.fiSharingSettingsContentComponent.newSelectionFilterModelName?.length
        );
    }

    public async getYesButtonClickPromise(): Promise<{ canClose: boolean }> {
        await this.fiSharingSettingsContentComponent.saveNewQuickSelection();
        return { canClose: true };
    }

    public getResult(): IItemSharingSettingsDialogResult {
        return {
            accessRightCoreFilter: this.itemAccessRightCoreFilter(),
            isShared: this.isShared(),
            savedQuickSelectionFilters: this.savedQuickSelectionFilters(),
            selectedFiltersCollection: this.selectedFiltersCollection(),
        };
    }

    public getNoButtonClickPromise(): Promise<{ canClose: boolean }> {
        return this.fiDialogsService.getCanCloseConfirmation(!this.fiValuesComparison.isChanged());
    }

    protected ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly FiInfoIconMode = FiInfoIconMode;
}
